import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { GTM_EVENTS } from 'src/shared/constants';
import { gtmUtils } from 'src/shared/toolbox';
import { registerContactForm } from 'src/store/contactForm';

import ContactFormAlert from './components/ContactFormAlert/ContactFormAlert';
import ContactFormButtonLoading from './components/ContactFormButtonLoading/ContactFormButtonLoading';
import { createContactFormSchema } from './ContactFormSchemaValidation';
import styles from './ContactForm.module.scss';

const ContactForm = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch<AppDispatch>();
	const [showAlertSucess, setShowAlertSucess] = useState(false);
	const [showAlertError, setShowAlertError] = useState(false);
	const {
		register,
		formState: { errors },
		handleSubmit,
		reset: resetForm
	} = useForm({
		mode: 'onTouched',
		resolver: yupResolver(createContactFormSchema(t))
	});

	const { isLoading, registerSuccessful, registerFailed } = useSelector((state: RootState) => state.contactForm);

	const onSubmit = (data: any) => {
		dispatch(registerContactForm(data));
		gtmUtils.pushDataLayerGTM(GTM_EVENTS.CONTACT_FORM_SUBMIT);
	};

	useEffect(() => {
		if (registerSuccessful) {
			setShowAlertSucess(true);
			resetForm();
			gtmUtils.pushDataLayerGTM(GTM_EVENTS.CONTACT_FORM_SUBMIT_SUCCESSFUL);
		}
	}, [registerSuccessful, resetForm]);

	useEffect(() => {
		if (registerFailed) {
			setShowAlertError(true);
			gtmUtils.pushDataLayerGTM(GTM_EVENTS.CONTACT_FORM_SUBMIT_FAILED);
		}
	}, [registerFailed]);

	return (
		<Row style={{ justifyContent: 'center' }}>
			<Col md={8}>
				<ContactFormAlert
					variant="success"
					title={t('contactSection.form.alerts.success.title')}
					body={t('contactSection.form.alerts.success.body')}
					footer={t('contactSection.form.alerts.success.footer')}
					show={(showAlertSucess && registerSuccessful) as boolean}
					onClose={() => setShowAlertSucess(false)}
				/>
				<ContactFormAlert
					variant="danger"
					title={t('contactSection.form.alerts.error.title')}
					body={t('contactSection.form.alerts.error.body')}
					footer={
						<>
							{t('contactSection.form.alerts.error.footer').split('info@cymetsolutions.com')[0]}
							<Alert.Link
								id="contact_form_email_alert_error_link"
								href="mailto:info@cymetsolutions.com"
								className={styles.formEmailAlertErrorLink}
							>
								info@cymetsolutions.com
							</Alert.Link>
							.
						</>
					}
					show={(showAlertError && registerFailed) as boolean}
					onClose={() => setShowAlertError(false)}
				/>
				<Form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
					<Row className={styles.formRow}>
						<FloatingLabel as={Col} md={6} controlId="floatingInputName" label={t('contactSection.form.labels.name')}>
							<Form.Control
								type="text"
								placeholder={t('contactSection.form.placeholders.name')}
								{...register('name')}
								isInvalid={!!errors.name}
							/>
							<Form.Control.Feedback type="invalid">{errors.name?.message as string}</Form.Control.Feedback>
						</FloatingLabel>

						<FloatingLabel
							as={Col}
							md={6}
							controlId="floatingInputLastName"
							label={t('contactSection.form.labels.lastName')}
						>
							<Form.Control
								type="text"
								placeholder={t('contactSection.form.placeholders.lastName')}
								{...register('lastName')}
								isInvalid={!!errors.lastName}
							/>
							<Form.Control.Feedback type="invalid">{errors.lastName?.message as string}</Form.Control.Feedback>
						</FloatingLabel>
					</Row>
					<Row className={styles.formRow}>
						<FloatingLabel as={Col} md={6} controlId="floatingInputEmail" label={t('contactSection.form.labels.email')}>
							<Form.Control
								type="text"
								placeholder={t('contactSection.form.placeholders.email')}
								{...register('email')}
								isInvalid={!!errors.email}
							/>
							<Form.Control.Feedback type="invalid">{errors.email?.message as string}</Form.Control.Feedback>
						</FloatingLabel>

						<FloatingLabel
							as={Col}
							md={6}
							controlId="floatingInputCompany"
							label={t('contactSection.form.labels.company')}
						>
							<Form.Control
								type="text"
								placeholder={t('contactSection.form.placeholders.company')}
								{...register('company')}
								isInvalid={!!errors.company}
							/>
							<Form.Control.Feedback type="invalid">{errors.company?.message as string}</Form.Control.Feedback>
						</FloatingLabel>
					</Row>
					<Row className={styles.formRow}>
						<FloatingLabel
							as={Col}
							md={12}
							controlId="floatingTextAreaMssage"
							label={t('contactSection.form.labels.message')}
						>
							<Form.Control
								as="textarea"
								placeholder={t('contactSection.form.placeholders.message')}
								style={{ height: '134px' }}
								{...register('message')}
								isInvalid={!!errors.message}
							/>
							<Form.Control.Feedback type="invalid">{errors.message?.message as string}</Form.Control.Feedback>
						</FloatingLabel>
					</Row>
					<Row className="justify-content-center">
						<Col xs={6} md={3} className="d-grid gap-2">
							<ContactFormButtonLoading
								id="contact_form_button"
								variant="secondary"
								textLoading={t('contactSection.form.submitting')}
								isLoading={isLoading}
							>
								{t('contactSection.form.submit')}
							</ContactFormButtonLoading>
						</Col>
					</Row>
				</Form>
			</Col>
		</Row>
	);
};

export default ContactForm;
